import React, { useState, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Text } from 'rebass'
import { BigNumber } from 'bignumber.js'

// utils
import { BIGNUMBER_FMT } from '../../../constants/bakery'

// components
import { RowBetween } from '../../../components/Row'
import PlayModal from './PlayModal'
import ArtworkBtn from '../../../components/nftInfo/button/ArtworkBtn'
import FlagStatus from '../../../components/nftInfo/FlagStatus'

// hooks
import { useActiveWeb3React } from '../../../hooks'
import useModal from '../../../hooks/bakery/useModal'
import useAllowanceERC20 from '../../../hooks/nft/useAllowanceERC20'
import useYam from '../../../hooks/bakery/useYam'
import useTokenBalance from '../../../hooks/bakery/useTokenBalance'
import { getSushiAddress } from '../../../sushi/utils'

import useUserBids from '../../../hooks/nft/useUserBids'
import useUserAsks from '../../../hooks/nft/useUserAsks'
import { SUPPORTTYPE } from '../../../constants/bakery/nft'
import { getBalanceNumber } from '../../../utils/bakery/formatBalance'

const VideosCard = styled.div<{bg?: string}>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${({bg, theme}) => (bg ? bg : theme.bsBg15)};
  border-radius: 28px;
  width: 375px;
  min-height: 468px;
  margin-bottom: 25px !important;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.bsBg27};
    box-shadow: 0px 2px 10px rgba(171, 133, 115, 0.16);
  }
  margin-right: 12px;
  margin-left: 12px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 318px;
    min-height: 413px;
  `}
`

const CardHead = styled.div`
  position: relative;
  width: 100%;
  .cover {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 347px;
    height: 347px;
    border-radius: 28px;
    background: rgba(0, 0, 0, 0);
    cursor: pointer;
    transition: background .2s linear;
    overflow: hidden;
  }
  .bgImg {
    opacity: 0;
    transition: opacity .2s linear;
  }
  &:hover {
    .cover {
      background: rgba(0, 0, 0, 0.8);
    }
    .bgImg {
      opacity: 1;
    }
  }
  .file-img {
    width: 347px;
    height: 347px;
    border-radius: 28px;
  }
  ${({ theme }) => theme.mediaWidth.upToMedium`
    .cover {
      width: 286px;
      height: 286px;
      background: rgba(0, 0, 0, 0.8);
    }
    .bgImg {
      width: 55px;
      opacity: 1;
    }
    .file-img {
      width: 286px;
      height: 286px;
    }
  `}
`
const IconImage = styled.img`
  display: block;
  width: 347px;
  height: 347px;
  border-radius: 28px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 286px;
    height: 286px;
  `}
`
const CardFoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  overflow: hidden;
`
const Title = styled.div<{color?:string}>`
  margin: 14px 0 4px 0;
  font-size: 16px;
  line-height: 44px;
  height: 44px;
  font-family: Yuanti SC;
  font-weight: 700;
  color: ${({ color, theme }) => color ? color : theme.bsText4};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 16px;
  `}
`
const VideoLevel = styled.div<{color?:string}>`
  margin: 14px 0 4px 0;
  font-size: 20px;
  font-family: Baloo Da;
  line-height: 44px;
  color: ${({ color, theme }) => color ? color : theme.bsText4};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 16px;
  `}
`

export interface BinanceProps {
  tokenId: string
  name: string
  description: string
  imageUrl: string
  price?: string
  nftType: string
  attributes: {
    animation_url: string
    cover_image: string
  }
  fileType: number
  fileUrl: string
  auctionPrice: string
  maxBidPrice: string
}

export default function Video({video, isMyBinanceNft, history, allowanceSellAll, nftType}: {video: BinanceProps, isMyBinanceNft?: boolean, history: any, allowanceSellAll?: boolean, nftType?: any}) {
  const level = useMemo(() => {
    return video['level']
  }, [video])

  const { account } = useActiveWeb3React()
  const allowanceBuy = useAllowanceERC20(nftType)

  const [bakeBalance, setBakeBalance] = useState(0)
  const yam = useYam()
  const bakeryBalance = useTokenBalance(getSushiAddress(yam))
  useEffect(() => {
    const balance = getBalanceNumber(bakeryBalance)
    setBakeBalance(balance)
  }, [yam, bakeryBalance])

  // 获取我的竞价
  const getBids = useUserBids(nftType)
  const emptyBids: any[] = []
  const [ids, setIds] = useState(emptyBids)
  const getIds = useCallback(async() => {
    const bids = await getBids()
    if (bids.length) {
      const bidsId = bids.map((d: any) => {
        return d[0]
      })
      setIds(bidsId)
    }
  }, [getBids])

  // 获取我的挂单
  const getAsks = useUserAsks(nftType)
  const emptyAsks: any[] = []
  const [aksIds, setAskIds] = useState(emptyAsks)
  const getAskIds = useCallback(async() => {
    const asks = await getAsks()
    if (asks.length) {
      const asksId = asks.map((d: any) => {
        return d[0]
      })
      setAskIds(asksId)
    }
  }, [getAsks])

  useEffect(() => {
    if (account) {
      getAskIds()
      getIds()
    }
  }, [getBids, getIds, getAskIds, getAsks, account, video])

  const [videoPlay] = useModal(
    <PlayModal
      nftType={nftType}
      video={video}
      isMyBinanceNft={isMyBinanceNft}
      bakeBalance={bakeBalance}
      allowanceBuy={allowanceBuy}
    />,
  )

  return (
    <VideosCard bg={level ==='v3' || isMyBinanceNft  ? '' : '#DEDEDE'}>
      <FlagStatus isMyArtwork={isMyBinanceNft} artwork={video} ids={ids} />
      <CardHead>
        {
          video.fileType === 3 ? <>
            <IconImage src={'images/bianceNft/' + video.name + '.jpg'} alt=""></IconImage>
            <div className="cover" onClick={videoPlay}>
              <img className="bgImg" src="images/bianceNft/play.svg" alt=""/>
            </div>
          </> : <img className="file-img" src={video.fileUrl} alt=""/>
        }
      </CardHead>
      <CardFoot>
        <RowBetween style={{alignItems: 'center'}}>
          <Title  color={level ==='v3'  || isMyBinanceNft ? '' : '#776D63'}>
            {video.name}
            <span style={{marginLeft: '10px'}}>{'#' + video.tokenId}</span>
          </Title>
          <VideoLevel color={level ==='v3'  || isMyBinanceNft ? '' : '#776D63'}>
            {video.price ? (new BigNumber(!video.auctionPrice ? video.price : video.maxBidPrice).integerValue(BigNumber.ROUND_CEIL).toFormat(0, BIGNUMBER_FMT)) : '-'}
            <Text fontSize={14} marginLeft="5px" style={{display: 'inline'}} fontFamily="Yuanti SC" fontWeight={700} color={level ==='v3'  || isMyBinanceNft ? '#722F0D' : '#776D63'}>BAKE</Text>
          </VideoLevel>
        </RowBetween>
        <ArtworkBtn
          artwork={video}
          status={1}
          isMyArtwork={isMyBinanceNft}
          ids={ids}
          myAksIds={aksIds}
          marketType={nftType}
          symbol={SUPPORTTYPE.BAKE}
        />
      </CardFoot>
    </VideosCard>
  )
}
